import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import '../../global.css';
import NeuralCanvas from '../../components/NeuralCanvas';
import ThemeToggle from '../../components/ThemeToggle';
import WordmarkLogo from '../../components/WordmarkLogo';
import OnboardingModal from '../../components/OnboardingModal';
import { logoutUser, fetchProfile } from '../../store/slices/authSlice';
import { fetchSubscription } from '../../store/slices/usageSlice';

const PAGE_META = {
  '/dashboard': { title: 'Overview', sub: 'Workspace at a glance' },
  '/dashboard/keys': { title: 'API Keys', sub: 'Manage authentication keys' },
  '/dashboard/usage': { title: 'Usage', sub: 'Token consumption & analytics' },
  '/dashboard/billing': { title: 'Billing', sub: 'Plan, credits & invoices' },
  '/dashboard/settings': { title: 'Settings', sub: 'Account & preferences' },
};

const NAV = [
  {
    label: 'WORKSPACE',
    items: [
      { to: '/dashboard', label: 'Overview', exact: true, icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1" y="1" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.3"/><rect x="8.5" y="1" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.3"/><rect x="1" y="8.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.3"/><rect x="8.5" y="8.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.3"/></svg> },
      { to: '/dashboard/keys', label: 'API Keys', icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="5.5" cy="6" r="3.2" stroke="currentColor" strokeWidth="1.3"/><path d="M7.8 8.2l5 5M11 11.5l1.5 1.5M9.5 10l1.5 1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
      { to: '/dashboard/usage', label: 'Usage', icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 13V8M5.5 13V5M9 13V7.5M12.5 13V3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
      { to: '/dashboard/billing', label: 'Billing', icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M1 6.5h13" stroke="currentColor" strokeWidth="1.3"/><rect x="3" y="9" width="3.5" height="1.5" rx="0.75" fill="currentColor" opacity="0.5"/></svg> },
    ],
  },
  {
    label: 'PRODUCT',
    items: [
      { to: '/dashboard/streaming', label: 'Live Streaming', icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><rect x="1" y="3.5" width="9" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3"/><path d="M10 6.5l4-2.5v7l-4-2.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg> },
      { to: '/chat', label: 'Chat', icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 2h11a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5l-3 3V3a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>, external: true },
      { to: '/docs', label: 'Docs', icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><path d="M2 2h7l4 4v7a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.3"/><path d="M9 2v4h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>, external: true },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { to: '/dashboard/settings', label: 'Settings', icon: <svg width="15" height="15" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.3"/><path d="M7.5 1v1.5M7.5 12.5V14M1 7.5h1.5M12.5 7.5H14M2.9 2.9l1.06 1.06M11.04 11.04l1.06 1.06M2.9 12.1l1.06-1.06M11.04 3.96l1.06-1.06" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
    ],
  },
];

const IconMenu = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18"/></svg>;
const IconX = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>;

function fmtTokens(n) {
  if (n == null) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return String(n);
}

function TokenGauge({ sub, loading }) {
  if (loading) return <div style={{ width: 160, height: 34, background: 'var(--line)', borderRadius: 6, animation: 'shimmer 1.4s ease-in-out infinite' }} />;
  if (!sub) return null;
  const pct = Math.min(100, Math.round((sub.tokensUsed / sub.monthlyTokenLimit) * 100));
  const trackColor = pct > 90 ? 'var(--error, #ef4444)' : pct > 70 ? '#f59e0b' : 'var(--accent)';
  return (
    <div className="token-gauge" title={`${fmtTokens(sub.tokensUsed)} of ${fmtTokens(sub.monthlyTokenLimit)} tokens used · ${pct}%`}>
      <div className="token-gauge-row">
        <span className="token-gauge-tier">{sub.tier}</span>
        <span className="token-gauge-count">{fmtTokens(sub.tokensUsed)} / {fmtTokens(sub.monthlyTokenLimit)}</span>
      </div>
      <div className="token-gauge-track">
        <div className="token-gauge-fill" style={{ width: `${pct}%`, background: trackColor }} />
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, accessToken, onboardingComplete } = useSelector(s => s.auth);
  const { subscription, subLoading } = useSelector(s => s.usage);
  const pageMeta = PAGE_META[location.pathname] || { title: 'Dashboard', sub: '' };
  const [sideOpen, setSideOpen] = useState(false);

  useEffect(() => {
    if (!accessToken) navigate('/login');
  }, [accessToken, navigate]);

  // Always fetch fresh profile on mount so businessId is valid even with old persisted tokens
  useEffect(() => {
    if (accessToken) {
      dispatch(fetchProfile());
      dispatch(fetchSubscription());
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    setSideOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 860) setSideOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = sideOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sideOpen]);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/login');
  };

  const firstName = user?.first_name || user?.firstName || user?.name?.split(' ')[0] || 'there';
  const lastName = user?.last_name || user?.lastName || '';
  const initials = ((firstName[0] || '') + (lastName[0] || '')).toUpperCase() || 'U';
  const email = user?.email || '';

  return (
    <div className="dash-root">
      <NeuralCanvas />

      {/* Mobile topbar */}
      <div className="dash-mobile-bar">
        <button className="dash-menu-btn" onClick={() => setSideOpen(o => !o)} aria-label="Open menu">
          <IconMenu />
        </button>
        <WordmarkLogo size={18} />
        <ThemeToggle />
      </div>

      {/* Backdrop */}
      {sideOpen && (
        <div onClick={() => setSideOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }} />
      )}

      {/* Sidebar */}
      <aside className={`dash-sidebar${sideOpen ? ' open' : ''}`}>
        <div className="dash-sidebar-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <WordmarkLogo size={20} />
          <button className="dash-menu-btn" onClick={() => setSideOpen(false)} style={{ display: 'none' }} aria-label="Close menu" id="dash-side-close">
            <IconX />
          </button>
        </div>

        {NAV.map(group => (
          <div key={group.label} className="dash-sidebar-section">
            <span className="dash-section-label">{group.label}</span>
            {group.items.map(item => (
              item.external ? (
                <Link key={item.to} to={item.to} className="dash-nav-item">
                  {item.icon}{item.label}
                </Link>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.exact}
                  className={({ isActive }) => `dash-nav-item${isActive ? ' active' : ''}`}
                >
                  {item.icon}{item.label}
                </NavLink>
              )
            ))}
            <div className="dash-nav-divider" />
          </div>
        ))}

        <div className="dash-sidebar-foot">
          <div className="dash-user-pill">
            <div className="dash-user-av">{initials}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="dash-user-name">{firstName} {lastName}</div>
              <div className="dash-user-role">{email || 'Developer'}</div>
            </div>
          </div>
          <button className="dash-signout" onClick={handleLogout}>Sign out</button>
        </div>
      </aside>

      {/* Main content */}
      <div className="dash-body">
        <header className="dash-topbar">
          <div className="dash-topbar-inner">
            <div>
              <div className="dash-breadcrumb">{pageMeta.title}</div>
              {pageMeta.sub && <div style={{ fontSize: 12, color: 'var(--fg-dim)', marginTop: 1 }}>{pageMeta.sub}</div>}
            </div>
            <div className="dash-topbar-actions">
              <TokenGauge sub={subscription} loading={subLoading} />
              <ThemeToggle />
              <Link to="/chat" className="btn btn-ghost" style={{ padding: '7px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1.5 1.5h10a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4l-2.5 2.5V2.5a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                Chat
              </Link>
            </div>
          </div>
        </header>

        <Outlet />
      </div>

      {/* Onboarding modal */}
      {accessToken && !onboardingComplete && <OnboardingModal />}
    </div>
  );
}
